// SectionPersonalityTypes.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SectionPersonalityTypes.module.css";

import breezeIcon from "../../assets/personality/breeze.png";
import tideIcon from "../../assets/personality/tide.png";
import stoneIcon from "../../assets/personality/stone.png";
import flameIcon from "../../assets/personality/flame.png";
import starIcon from "../../assets/personality/star.png";

const TYPES = ["breeze", "tide", "stone", "flame", "star"];

const ICONS = {
  breeze: breezeIcon,
  tide: tideIcon,
  stone: stoneIcon,
  flame: flameIcon,
  star: starIcon,
};

export default function SectionPersonalityTypes() {
  const { t } = useTranslation("personalityTypes");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const firstFocusRef = useRef(null);
  const lastActiveElement = useRef(null);

  useEffect(() => {
    if (open) {
      lastActiveElement.current = document.activeElement;
      setTimeout(() => firstFocusRef.current?.focus(), 80);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      lastActiveElement.current?.focus?.();
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "Tab" && open) {
        // minimal focus trap inside modal wrapper
        const modal = document.getElementById("pt-modal");
        if (!modal) return;
        const focusable = Array.from(
          modal.querySelectorAll(
            'a,button,input,textarea,[tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function openModal(type) {
    setActive(type);
    setOpen(true);
  }
  function closeModal() {
    setOpen(false);
    setActive(null);
  }

  return (
    <section className={styles.section} aria-labelledby="types-title">
      <div className={styles.header}>
        <h2 id="types-title">{t("section_title")}</h2>
        <p className={styles.subtitle}>{t("section_subtitle")}</p>
      </div>

      <div className={styles.grid}>
        {TYPES.map((type) => (
          <article
            key={type}
            className={styles.card}
            onClick={() => openModal(type)}
          >
            <img
              src={ICONS[type]}
              alt={t(`${type}.title`)}
              className={styles.cardImage}
              aria-hidden={false}
            />
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{t(`${type}.title`)}</h3>
              <p className={styles.cardEssence}>{t(`${type}.essence_short`)}</p>
            </div>

            <div className={styles.cardActions}>
              <button
                className={styles.cardBtn}
                onClick={() => openModal(type)}
                aria-haspopup="dialog"
                aria-controls="pt-modal"
              >
                {t("read_more")}
              </button>
            </div>
          </article>
        ))}
      </div>

      {/* Modal */}
      <div
        id="pt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pt-modal-title"
        className={`${styles.modalWrap} ${open ? styles.open : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div className={styles.modal} aria-live="polite">
          {active && (
            <>
              <header className={styles.modalHeader}>
                <div className={styles.modalHeaderLeft}>
                  <img
                    src={ICONS[active]}
                    alt={t(`${active}.title`)}
                    className={styles.modalThumb}
                  />
                  <div>
                    <h3 id="pt-modal-title" className={styles.modalTitle}>
                      {t(`${active}.title`)}
                    </h3>
                    <p className={styles.modalSubtitle}>
                      {t(`${active}.essence`)}
                    </p>
                    <p className={styles.modalSubtitle}>
                      {t(`${active}.element`)}
                    </p>
                  </div>
                </div>

                <button
                  className={styles.modalClose}
                  onClick={closeModal}
                  aria-label={t("close")}
                  ref={firstFocusRef}
                >
                  ✕
                </button>
              </header>

              <div className={styles.modalBody}>
                <p className={styles.label}>
                  <strong>{t("profiles_label")}</strong>
                </p>
                <p>{t(`${active}.profiles`)}</p>

                <p className={styles.label}>
                  <strong>{t("characteristic_label")}</strong>
                </p>
                <p>{t(`${active}.characteristic`)}</p>

                <p className={styles.label}>
                  <strong>{t("psychology_label")}</strong>
                </p>
                <p>{t(`${active}.psychology`)}</p>

                <p className={styles.label}>
                  <strong>{t("qualities_label")}</strong>
                </p>
                <p>{t(`${active}.qualities`)}</p>

                <p className={styles.label}>
                  <strong>{t("challenges_label")}</strong>
                </p>
                <p>{t(`${active}.challenges`)}</p>
              </div>

              <footer className={styles.modalFooter}>
                <button className={styles.secondary} onClick={closeModal}>
                  {t("close")}
                </button>
                <a
                  href="/personality-quiz"
                  className={styles.cta}
                  onClick={closeModal}
                >
                  {t("cta_start_test")}
                </a>
              </footer>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
